import { getFeatures } from '@/apis/features';
import { UAT_HOST } from '@/constants';
import { LiveChatConfig, LiveChatScript, LiveChatUrl } from '@/enums';
import { useAppDispatch, useAppSelector } from '@/hook';
import { getOrigin } from '@/hooks/useRouteSection';
import LiveChatImg from '@/images/live_chat.png';
import { useLocation } from '@reach/router';
import { Script } from 'gatsby';
import { useEffect, useMemo } from 'react';
import styled from 'styled-components';

const MaychatScript = ({
  url,
  config,
}: {
  url: string;
  config: LiveChatConfig;
}) => {
  return config === LiveChatConfig.NEW_TAB ? (
    <ChatIcon href={url} target="_blank"></ChatIcon>
  ) : (
    <Script id="chatbox">
      {`
        var LHCChatOptions = {};
        LHCChatOptions.opt = {widget_height:340,widget_width:300,popup_height:520,popup_width:500};
        (function() {
          // Additional Data Values
          LHCChatOptions.attr = new Array();
          LHCChatOptions.attr.push({'name':'Channel','value':'ETIQA Web'});  
          LHCChatOptions.attr.push({'name':'Stage','value':'Pre Login'}); 

          var po = document.createElement('script'); po.type = 'text/javascript'; po.async = true;
          var referrer = (document.referrer) ? encodeURIComponent(document.referrer.substr(document.referrer.indexOf('://')+1)) : '';
          var location  = (document.location) ? encodeURIComponent(window.location.href.substring(window.location.protocol.length)) : '';
          po.src = '${url}?tenant=ETIQA&r='+referrer+'&l='+location;
          var s = document.getElementsByTagName('script')[0]; s.parentNode.insertBefore(po, s);
        })();
      `}
    </Script>
  );
};

const LiveChatBox = () => {
  const dispatch = useAppDispatch();

  const location = useLocation();
  const origin = getOrigin() || '';
  const lowerOrigin = origin.toLowerCase();
  const isNonProd =
    lowerOrigin.includes(UAT_HOST.toLowerCase()) ||
    lowerOrigin.includes('localhost');
  const isProd = !isNonProd;
  const isQuotation = location.pathname.includes('quotation');

  const showSummary = useAppSelector((state) => state.uiState.showSummary);
  const config = useAppSelector(
    (state) => state.uiState.features?.LiveChat ?? LiveChatConfig.POP_UP,
  );

  const urls = useMemo(
    () =>
      ({
        [LiveChatConfig.NEW_TAB]: LiveChatUrl,
        [LiveChatConfig.POP_UP]: LiveChatScript,
      }[config]),
    [config],
  );

  const arrUrl = useMemo(
    () =>
      !isProd ? [urls.UatExternal, urls.UatInternal] : [urls.ProdExternal],
    [urls],
  );

  useEffect(() => {
    dispatch(getFeatures());
  }, []);

  useEffect(() => {
    const el = document.querySelector('#chatbox-style');
    el?.remove();

    if (!isQuotation) {
      const style = document.createElement('style');
      style.id = 'chatbox-style';
      style.innerHTML = `
        #launcher, 
        [title="Number of unread messages"], 
        [title="Close message"], 
        [title="Message from company"] {
          transform: translateY(-5.5rem) !important;
          display: ${showSummary ? 'none' : 'block'} !important;
        }
        `;
      document.head.append(style);
    }
  }, [isQuotation, showSummary]);

  return (
    <>
      {arrUrl.map((url) =>
        url !== '' ? (
          <MaychatScript key={url} url={url} config={config} />
        ) : null,
      )}
    </>
  );
};

const ChatIcon = styled.a`
  display: block;
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  padding: 12px;
  height: 52px;
  width: 52px;
  background-image: url(${LiveChatImg});
  background-repeat: no-repeat;
  background-position: center center;
  background-color: #ffbf00;
  border: 2px solid #e3e3e3;
  border-radius: 47px;
  border-color: #f9f9f9;
  box-shadow: 0 0 17px rgba(50, 50, 50, 0.5);
  text-decoration: none;
  font-size: 38px;
`;

export default LiveChatBox;
